import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar, LinearProgress,
  Select, MenuItem, FormControl, Divider, Snackbar, Alert, CircularProgress,
  Checkbox, FormControlLabel, Skeleton,
} from '@mui/material';
import {
  Users, ArrowLeft, Plus, X, Star, Shield, CheckCircle2, AlertTriangle,
  TrendingUp, Brain, Code, MessageSquare, Target, Award, Crown,
  FileText, Download, BarChart3, Eye, Lock, RefreshCw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip as ReTooltip,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import ScoreRing from '../components/analytics/ScoreRing';
import DonutChart from '../components/analytics/DonutChart';

/* ── Constants ─────────────────────────────────────────────────────────── */

const CANDIDATE_COLORS = ['#6366f1', '#4F46E5', '#f59e0b', '#ec4899', '#8b5cf6'];

const RECOMMENDATION_CONFIG = {
  'Highly Recommended': { bg: '#dcfce7', color: '#16a34a', icon: <CheckCircle2 size={12} /> },
  'Recommended':        { bg: '#dbeafe', color: '#2563eb', icon: <CheckCircle2 size={12} /> },
  'Consider':           { bg: '#fef9c3', color: '#ca8a04', icon: <AlertTriangle size={12} /> },
  'Hold':               { bg: '#fff7ed', color: '#ea580c', icon: <AlertTriangle size={12} /> },
  'Not Recommended':    { bg: '#fee2e2', color: '#dc2626', icon: <X size={12} /> },
};

const TRUST_LEVEL_CONFIG = {
  low:    { label: 'Low Risk',    color: '#4F46E5', bg: '#d1fae5' },
  medium: { label: 'Medium Risk', color: '#f59e0b', bg: '#fef3c7' },
  high:   { label: 'High Risk',   color: '#ef4444', bg: '#fee2e2' },
};

const getTrustConfig = (riskLevel) => {
  if (!riskLevel) return TRUST_LEVEL_CONFIG.low;
  const key = riskLevel.toLowerCase().replace(/\s+risk$/, '').trim();
  return TRUST_LEVEL_CONFIG[key] || TRUST_LEVEL_CONFIG.low;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

const MotionDiv = motion.div;

/* ── ScoreBar ──────────────────────────────────────────────────────────── */
function ScoreBar({ value, color }) {
  const safeVal = value != null ? Math.round(value) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1, minWidth: 0 }}>
      <LinearProgress
        variant="determinate"
        value={safeVal}
        sx={{
          flex: 1, height: 7, borderRadius: 4, bgcolor: '#f1f5f9',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#374151', minWidth: 28, textAlign: 'right' }}>
        {value != null ? safeVal : '—'}
      </Typography>
    </Box>
  );
}

/* ── Skeleton card while loading ─────────────────────────────────────── */
function CandidateSkeleton() {
  return (
    <Card elevation={0} sx={{ borderRadius: '18px', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="circular" width={72} height={72} />
          <Skeleton variant="rounded" width={100} height={22} />
        </Box>
      </CardContent>
    </Card>
  );
}

/* ── Top candidate card ──────────────────────────────────────────────── */
function CandidateTopCard({ candidate, isTop, colorIdx, onRemove }) {
  const recCfg   = RECOMMENDATION_CONFIG[candidate.recommendation] || RECOMMENDATION_CONFIG['Consider'];
  const trustCfg = getTrustConfig(candidate.riskLevel);
  const color    = CANDIDATE_COLORS[colorIdx % CANDIDATE_COLORS.length];

  return (
    <MotionDiv
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.94 }}
      transition={{ duration: 0.35 }}
      layout
    >
      <Card elevation={0} sx={{
        borderRadius: '18px',
        border: isTop ? `2px solid ${color}` : '1.5px solid #f1f5f9',
        background: isTop ? `linear-gradient(135deg, ${color}08, ${color}03)` : '#fff',
        boxShadow: isTop ? `0 0 0 4px ${color}15, 0 8px 24px rgba(0,0,0,0.07)` : 'none',
        position: 'relative', height: '100%',
      }}>
        {isTop && (
          <Box sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}>
            <Chip
              label="AI Recommended"
              size="small"
              icon={<Star size={10} color="#7c3aed" />}
              sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontWeight: 700, fontSize: '0.62rem', boxShadow: '0 2px 8px rgba(124,58,237,0.2)' }}
            />
          </Box>
        )}
        <Button
          size="small"
          onClick={() => onRemove(candidate.application_id)}
          sx={{
            position: 'absolute', top: 6, right: 6, minWidth: 0, p: 0.5,
            color: '#9ca3af', borderRadius: '6px',
            '&:hover': { color: '#ef4444', bgcolor: '#fee2e2' },
          }}
        >
          <X size={14} />
        </Button>

        <CardContent sx={{ p: 2.5, pt: isTop ? 3.5 : 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1 }}>
            <Avatar sx={{
              width: 56, height: 56, bgcolor: color, fontSize: '1.15rem', fontWeight: 700,
              boxShadow: `0 4px 12px ${color}44`,
            }}>
              {getInitials(candidate.name)}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#111827' }}>
                {candidate.name}
              </Typography>
              <Typography sx={{ fontSize: '0.73rem', color: '#6b7280', mt: 0.2 }}>{candidate.role}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af' }}>{candidate.email}</Typography>
            </Box>

            <ScoreRing score={candidate.overallScore || 0} size={72} strokeWidth={6} />

            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Chip
                label={trustCfg.label}
                size="small"
                sx={{ bgcolor: trustCfg.bg, color: trustCfg.color, fontWeight: 600, fontSize: '0.62rem', height: 20 }}
              />
              <Chip
                label={candidate.recommendation}
                size="small"
                icon={recCfg.icon}
                sx={{ bgcolor: recCfg.bg, color: recCfg.color, fontWeight: 600, fontSize: '0.62rem', height: 20, '& .MuiChip-icon': { color: recCfg.color } }}
              />
            </Box>

            {/* Mini interview completion bar */}
            <Box sx={{ width: '100%', px: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#9ca3af' }}>Interview Progress</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#6b7280', fontWeight: 600 }}>
                  {candidate.interviewCompletion}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={candidate.interviewCompletion || 0}
                sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 } }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </MotionDiv>
  );
}

/* ── Comparison Matrix Row ─────────────────────────────────────────────── */
const METRIC_MAP = {
  'AI Score':            c => c.aiScore,
  'Technical Score':     c => c.technicalScore,
  'Coding Score':        c => c.codingScore,
  'Communication':       c => c.communicationScore,
  'HR Score':            c => c.hrScore,
  'Trust Score':         c => c.trustScore,
  'Match Score':         c => c.matchScore,
  'Interview Completion': c => c.interviewCompletion,
};

const METRIC_ICONS = {
  'AI Score':            <Brain size={14} />,
  'Technical Score':     <Code size={14} />,
  'Coding Score':        <Code size={14} />,
  'Communication':       <MessageSquare size={14} />,
  'HR Score':            <Users size={14} />,
  'Trust Score':         <Shield size={14} />,
  'Match Score':         <Target size={14} />,
  'Interview Completion': <CheckCircle2 size={14} />,
};

function MatrixRow({ metric, candidates, colors }) {
  const values = candidates.map(c => METRIC_MAP[metric]?.(c) ?? null);
  const validValues = values.filter(v => v != null);
  const maxVal = validValues.length > 0 ? Math.max(...validValues) : 0;

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: `160px repeat(${candidates.length}, 1fr)`,
      gap: 1.5, alignItems: 'center', py: 1.2,
      borderBottom: '1px solid #f8fafc', '&:last-child': { borderBottom: 'none' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <Box sx={{ color: '#6b7280' }}>{METRIC_ICONS[metric]}</Box>
        <Typography sx={{ fontSize: '0.77rem', fontWeight: 600, color: '#374151' }}>{metric}</Typography>
      </Box>
      {candidates.map((c, i) => {
        const val = values[i];
        const isMax = val != null && val === maxVal && validValues.length > 1;
        return (
          <Box key={c.application_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScoreBar value={val} color={isMax ? colors[i % colors.length] : '#cbd5e1'} />
            {isMax && <Star size={10} color="#f59e0b" fill="#f59e0b" style={{ flexShrink: 0 }} />}
          </Box>
        );
      })}
    </Box>
  );
}

/* ── Candidate selection panel ─────────────────────────────────────────── */
function CandidateSelector({ candidates, selectedIds, onToggle, maxReached }) {
  if (candidates.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Users size={36} color="#d1d5db" />
        <Typography sx={{ color: '#9ca3af', mt: 1, fontSize: '0.875rem' }}>
          No candidates found for this job yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mb: 1.5 }}>
        Select 2–5 candidates to compare. ({selectedIds.length} selected)
      </Typography>
      <Grid container spacing={1.5}>
        {candidates.map((c) => {
          const checked = selectedIds.includes(c.application_id);
          const disabled = !checked && maxReached;
          const trustCfg = getTrustConfig(c.riskLevel);
          const recCfg = RECOMMENDATION_CONFIG[c.recommendation] || RECOMMENDATION_CONFIG['Consider'];

          return (
            <Grid item xs={12} sm={6} md={4} key={c.application_id}>
              <Card
                elevation={0}
                onClick={() => !disabled && onToggle(c.application_id)}
                sx={{
                  borderRadius: '14px',
                  border: checked ? '2px solid #6366f1' : '1.5px solid #f1f5f9',
                  bgcolor: checked ? '#f5f3ff' : disabled ? '#fafafa' : '#fff',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.55 : 1,
                  transition: 'all 0.15s',
                  '&:hover': !disabled ? { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderColor: checked ? '#6366f1' : '#c7d2fe' } : {},
                }}
              >
                <CardContent sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    size="small"
                    sx={{ p: 0, color: '#6366f1', '&.Mui-checked': { color: '#6366f1' } }}
                    onClick={e => e.stopPropagation()}
                    onChange={() => !disabled && onToggle(c.application_id)}
                  />
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#6366f1', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>
                    {getInitials(c.name)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                      <Chip label={`Score: ${c.overallScore || 'N/A'}`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 700 }} />
                      <Chip label={trustCfg.label} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: trustCfg.bg, color: trustCfg.color, fontWeight: 600 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

/* ── Empty comparison state ────────────────────────────────────────────── */
function EmptyComparisonState({ onBack }) {
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '18px', mx: 'auto', mb: 2.5,
          background: 'linear-gradient(135deg, #e0e7ff, #f5f3ff)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Users size={30} color="#6366f1" />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827', mb: 0.8 }}>
          Select Candidates to Compare
        </Typography>
        <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', maxWidth: 380, mx: 'auto', mb: 3 }}>
          Choose a job role above, then select 2–5 candidates from that job's applications to begin the AI-powered comparison.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={15} />}
          onClick={onBack}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: '#e5e7eb', color: '#374151' }}
        >
          Back to Analytics
        </Button>
      </motion.div>
    </Box>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function CandidateComparison() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [jobs, setJobs]               = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState('');

  const [allCandidates, setAllCandidates]   = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState('');

  const [selectedIds, setSelectedIds] = useState([]);
  const [comparing, setComparing]     = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

  const isProfessional = user?.plan === 'professional' || user?.plan === 'annual';

  /* ── Load jobs on mount ── */
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await api.get('/admin/jobs');
        const list = res.data?.jobs || [];
        setJobs(list.filter(j => j.status === 'active' || j.is_active));
      } catch {
        setJobs([]);
      } finally {
        setJobsLoading(false);
      }
    };
    fetchJobs();
  }, []);

  /* ── Fetch candidates when job changes ── */
  useEffect(() => {
    if (!selectedJobId) {
      setAllCandidates([]);
      setSelectedIds([]);
      setComparing(false);
      return;
    }

    const fetchCandidates = async () => {
      setCandidatesLoading(true);
      setCandidatesError('');
      setSelectedIds([]);
      setComparing(false);
      try {
        const res = await api.get(`/admin/jobs/${selectedJobId}/comparison-candidates`);
        setAllCandidates(res.data?.candidates || []);
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to load candidates.';
        setCandidatesError(msg);
        setAllCandidates([]);
      } finally {
        setCandidatesLoading(false);
      }
    };

    fetchCandidates();
  }, [selectedJobId]);

  /* ── Toggle candidate selection ── */
  const handleToggle = useCallback((applicationId) => {
    setSelectedIds(prev => {
      if (prev.includes(applicationId)) {
        return prev.filter(id => id !== applicationId);
      }
      if (prev.length >= 5) {
        setSnackbar({ open: true, message: 'Maximum 5 candidates allowed for comparison', severity: 'warning' });
        return prev;
      }
      return [...prev, applicationId];
    });
  }, []);

  /* ── Remove from comparison view ── */
  const handleRemove = useCallback((applicationId) => {
    setSelectedIds(prev => {
      const next = prev.filter(id => id !== applicationId);
      if (next.length < 2) setComparing(false);
      return next;
    });
  }, []);

  const selectedCandidates = useMemo(
    () => allCandidates.filter(c => selectedIds.includes(c.application_id)),
    [allCandidates, selectedIds],
  );

  const topCandidate = useMemo(
    () => selectedCandidates.reduce((best, c) =>
      (c.overallScore || 0) > (best?.overallScore || 0) ? c : best, null),
    [selectedCandidates],
  );

  const radarData = useMemo(() => {
    const metrics = ['aiScore', 'technicalScore', 'codingScore', 'communicationScore', 'trustScore'];
    const labels  = ['AI Score', 'Technical', 'Coding', 'Communication', 'Trust'];
    return labels.map((label, i) => {
      const entry = { metric: label };
      selectedCandidates.forEach(c => { entry[c.name] = c[metrics[i]] ?? 0; });
      return entry;
    });
  }, [selectedCandidates]);

  const trustDonutData = useMemo(() => {
    const counts = { low: 0, medium: 0, high: 0 };
    selectedCandidates.forEach(c => {
      const key = (c.riskLevel || 'low').toLowerCase().replace(/\s+risk$/, '').trim();
      if (counts[key] !== undefined) counts[key]++;
    });
    const total = selectedCandidates.length || 1;
    return [
      { label: 'Low Risk',    value: Math.round((counts.low    / total) * 100), color: '#4F46E5' },
      { label: 'Medium Risk', value: Math.round((counts.medium / total) * 100), color: '#f59e0b' },
      { label: 'High Risk',   value: Math.round((counts.high   / total) * 100), color: '#ef4444' },
    ].filter(d => d.value > 0);
  }, [selectedCandidates]);

  /* ── Dynamic recommendation text ── */
  const recommendationText = useMemo(() => {
    if (!topCandidate) return '';
    const trustCfg = getTrustConfig(topCandidate.riskLevel);
    const strengths = [];
    if (topCandidate.aiScore)         strengths.push(`AI score (${topCandidate.aiScore}/100)`);
    if (topCandidate.technicalScore)  strengths.push(`technical performance (${topCandidate.technicalScore}/100)`);
    if (topCandidate.communicationScore) strengths.push(`communication`);
    if (topCandidate.riskLevel)       strengths.push(`${trustCfg.label.toLowerCase()} interview behavior`);
    return `${topCandidate.name} is the strongest candidate based on ${strengths.join(', ') || 'overall performance'}.`;
  }, [topCandidate]);

  /* ── Access guard ── */
  if (!isProfessional) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', px: 3, py: 8, textAlign: 'center' }}>
        <motion.div initial={{ opacity: 0, scale: 0.93 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '20px', mx: 'auto', mb: 3,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(99,102,241,0.35)',
          }}>
            <Lock size={36} color="white" />
          </Box>
          <Chip label="PRO FEATURE" size="small" sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontWeight: 700, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
            Candidate Comparison is a Professional Feature
          </Typography>
          <Typography sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
            Compare up to 5 real candidates side-by-side with AI scoring, trust analysis, and hiring recommendations — available on the Professional (Annual) plan.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Crown size={16} />}
              onClick={() => navigate('/pricing?plan=professional')}
              sx={{
                borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
              }}
            >
              Upgrade to Professional
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/analytics')}
              sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderColor: '#e5e7eb', color: '#374151' }}
            >
              Back to Analytics
            </Button>
          </Box>
        </motion.div>
      </Box>
    );
  }

  const maxReached = selectedIds.length >= 5;
  const canCompare = selectedIds.length >= 2;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>

      {/* ── Header ── */}
      <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            size="small"
            startIcon={<ArrowLeft size={14} />}
            onClick={() => navigate('/analytics')}
            sx={{ textTransform: 'none', color: '#6b7280', fontWeight: 500, mb: 1.5, '&:hover': { bgcolor: '#f8fafc' } }}
          >
            Back to Analytics
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
                  Candidate Comparison
                </Typography>
                <Chip
                  label="PRO"
                  size="small"
                  icon={<Crown size={11} />}
                  sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontWeight: 700, fontSize: '0.65rem', '& .MuiChip-icon': { color: '#7c3aed' } }}
                />
              </Box>
              <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Compare candidates side by side and identify the best fit for the role.
              </Typography>
            </Box>

            {/* Job Role Selector */}
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <Select
                value={selectedJobId}
                onChange={e => setSelectedJobId(e.target.value)}
                displayEmpty
                disabled={jobsLoading}
                sx={{ borderRadius: '10px', fontSize: '0.875rem', bgcolor: '#fff' }}
                renderValue={val => {
                  if (!val) return <span style={{ color: '#9ca3af' }}>Select a job role…</span>;
                  const j = jobs.find(j => j.id === val);
                  return j?.title || val;
                }}
              >
                {jobsLoading && <MenuItem disabled><CircularProgress size={14} sx={{ mr: 1 }} />Loading jobs…</MenuItem>}
                {!jobsLoading && jobs.length === 0 && <MenuItem disabled>No active jobs found</MenuItem>}
                {jobs.map(j => (
                  <MenuItem key={j.id} value={j.id} sx={{ fontSize: '0.875rem' }}>{j.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </motion.div>

      {/* ── No job selected ── */}
      {!selectedJobId && <EmptyComparisonState onBack={() => navigate('/analytics')} />}

      {/* ── Candidates loading ── */}
      {selectedJobId && candidatesLoading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} md={4} key={i}><CandidateSkeleton /></Grid>
          ))}
        </Grid>
      )}

      {/* ── Candidates error ── */}
      {selectedJobId && !candidatesLoading && candidatesError && (
        <Alert severity="error" sx={{ borderRadius: '12px', mb: 3 }}
          action={<Button size="small" onClick={() => setSelectedJobId(s => { const v = s; setSelectedJobId(''); setTimeout(() => setSelectedJobId(v), 50); })}><RefreshCw size={14} /></Button>}>
          {candidatesError}
        </Alert>
      )}

      {/* ── Candidate Selection Panel ── */}
      {selectedJobId && !candidatesLoading && !candidatesError && !comparing && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <Card elevation={0} sx={{ borderRadius: '18px', border: '1px solid #f1f5f9', mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Users size={18} color="#6366f1" />
                  <Typography sx={{ fontWeight: 700, color: '#111827' }}>
                    Select Candidates
                  </Typography>
                  <Chip
                    label={`${allCandidates.length} available`}
                    size="small"
                    sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.65rem' }}
                  />
                </Box>
                {canCompare && (
                  <Button
                    variant="contained"
                    startIcon={<BarChart3 size={15} />}
                    onClick={() => setComparing(true)}
                    sx={{
                      borderRadius: '10px', textTransform: 'none', fontWeight: 700,
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                      '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                    }}
                  >
                    Compare {selectedIds.length} Candidates
                  </Button>
                )}
              </Box>
              <CandidateSelector
                candidates={allCandidates}
                selectedIds={selectedIds}
                onToggle={handleToggle}
                maxReached={maxReached}
              />
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Comparison Dashboard ── */}
      {comparing && selectedCandidates.length >= 2 && (
        <>
          {/* Back to selection */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small"
              startIcon={<ArrowLeft size={13} />}
              onClick={() => setComparing(false)}
              sx={{ textTransform: 'none', color: '#6b7280', fontWeight: 500, '&:hover': { bgcolor: '#f8fafc' } }}
            >
              Modify Selection
            </Button>
            <Chip label={`Comparing ${selectedCandidates.length} candidates`} size="small" sx={{ bgcolor: '#f0f4ff', color: '#4f46e5', fontWeight: 600, fontSize: '0.7rem' }} />
          </Box>

          {/* Candidate Top Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <AnimatePresence mode="popLayout">
              {selectedCandidates.map((candidate, i) => (
                <Grid item xs={12} sm={6} md={Math.max(2, Math.floor(12 / selectedCandidates.length))} key={candidate.application_id}>
                  <CandidateTopCard
                    candidate={candidate}
                    isTop={candidate.application_id === topCandidate?.application_id && selectedCandidates.length > 1}
                    colorIdx={i}
                    onRemove={handleRemove}
                  />
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>

          {/* AI Recommendation Banner */}
          {topCandidate && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.35 }}>
              <Card elevation={0} sx={{
                borderRadius: '18px', mb: 3,
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                border: '1px solid #3730a3',
                boxShadow: '0 8px 32px rgba(30,27,75,0.25)',
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: '13px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Brain size={22} color="white" />
                    </Box>
                    <Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', fontWeight: 600, mb: 0.3, letterSpacing: '0.05em' }}>
                        AI RECOMMENDATION
                      </Typography>
                      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.55 }}>
                        {recommendationText}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<Eye size={13} />}
                    onClick={() => navigate(`/job-interview-history?applicationId=${topCandidate.application_id}`)}
                    sx={{
                      borderRadius: '10px', textTransform: 'none', fontWeight: 700, flexShrink: 0,
                      borderColor: 'rgba(255,255,255,0.3)', color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.5)' },
                    }}
                  >
                    View Report
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Comparison Matrix */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.4 }}>
            <Card elevation={0} sx={{ borderRadius: '18px', border: '1px solid #f1f5f9', mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 }, overflowX: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <BarChart3 size={17} color="#6366f1" />
                  <Typography sx={{ fontWeight: 800, color: '#111827' }}>Score Comparison</Typography>
                </Box>

                {/* Column headers */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `160px repeat(${selectedCandidates.length}, 1fr)`,
                  gap: 1.5, mb: 1, pb: 1.5, borderBottom: '2px solid #f1f5f9',
                }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Metric</Typography>
                  {selectedCandidates.map((c, i) => (
                    <Box key={c.application_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CANDIDATE_COLORS[i % CANDIDATE_COLORS.length], flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.77rem', fontWeight: 700, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name.split(' ')[0]}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {Object.keys(METRIC_MAP).map(metric => (
                  <MatrixRow key={metric} metric={metric} candidates={selectedCandidates} colors={CANDIDATE_COLORS} />
                ))}

                {/* Recommendation row */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `160px repeat(${selectedCandidates.length}, 1fr)`,
                  gap: 1.5, alignItems: 'center', pt: 1.5, mt: 1, borderTop: '2px solid #f1f5f9',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Award size={14} color="#6b7280" />
                    <Typography sx={{ fontSize: '0.77rem', fontWeight: 700, color: '#374151' }}>Recommendation</Typography>
                  </Box>
                  {selectedCandidates.map(c => {
                    const cfg = RECOMMENDATION_CONFIG[c.recommendation] || {};
                    return (
                      <Box key={c.application_id}>
                        <Chip label={c.recommendation} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.62rem', height: 22 }} />
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </motion.div>

          {/* Skills + Trust row */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {/* Radar / skills */}
            <Grid item xs={12} md={7}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38, duration: 0.4 }}>
                <Card elevation={0} sx={{ borderRadius: '18px', border: '1px solid #f1f5f9', height: '100%' }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Target size={17} color="#6366f1" />
                      <Typography sx={{ fontWeight: 800, color: '#111827' }}>Skill Radar</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                      {selectedCandidates.map((c, i) => (
                        <Box key={c.application_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: CANDIDATE_COLORS[i % CANDIDATE_COLORS.length] }} />
                          <Typography sx={{ fontSize: '0.73rem', color: '#374151', fontWeight: 500 }}>{c.name.split(' ')[0]}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <ResponsiveContainer width="100%" height={260}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#f0f4f8" />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                        {selectedCandidates.map((c, i) => (
                          <Radar
                            key={c.application_id} name={c.name.split(' ')[0]} dataKey={c.name}
                            stroke={CANDIDATE_COLORS[i % CANDIDATE_COLORS.length]}
                            fill={CANDIDATE_COLORS[i % CANDIDATE_COLORS.length]}
                            fillOpacity={0.12} strokeWidth={2}
                          />
                        ))}
                        <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 12 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Trust Analysis */}
            <Grid item xs={12} md={5}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42, duration: 0.4 }}>
                <Card elevation={0} sx={{ borderRadius: '18px', border: '1px solid #f1f5f9', height: '100%' }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Shield size={17} color="#6366f1" />
                      <Typography sx={{ fontWeight: 800, color: '#111827' }}>Trust & Integrity</Typography>
                    </Box>

                    {trustDonutData.length > 0 ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <DonutChart data={trustDonutData} size={130} label="Trust" />
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2, color: '#9ca3af', fontSize: '0.8rem' }}>
                        No trust data available
                      </Box>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {selectedCandidates.map((c, i) => {
                      const trustCfg = getTrustConfig(c.riskLevel);
                      return (
                        <Box key={c.application_id} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CANDIDATE_COLORS[i % CANDIDATE_COLORS.length], flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.77rem', fontWeight: 600, color: '#374151' }}>
                              {c.name.split(' ')[0]}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            {c.riskLevel ? (
                              <Chip label={trustCfg.label} size="small" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, bgcolor: trustCfg.bg, color: trustCfg.color }} />
                            ) : (
                              <Chip label="No data" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f9fafb', color: '#9ca3af' }} />
                            )}
                          </Box>
                          {c.trustScore != null && (
                            <Box sx={{ pl: 2, mt: 0.4 }}>
                              <ScoreBar value={c.trustScore} color={trustCfg.color} />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Score breakdown per candidate */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48, duration: 0.4 }}>
            <Card elevation={0} sx={{ borderRadius: '18px', border: '1px solid #f1f5f9', mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <TrendingUp size={17} color="#6366f1" />
                  <Typography sx={{ fontWeight: 800, color: '#111827' }}>Score Breakdown by Candidate</Typography>
                </Box>
                <Grid container spacing={2}>
                  {selectedCandidates.map((c, i) => {
                    const color = CANDIDATE_COLORS[i % CANDIDATE_COLORS.length];
                    const scoreRows = [
                      { label: 'AI Interview',   value: c.aiScore,           icon: <Brain size={13} /> },
                      { label: 'Technical',       value: c.technicalScore,     icon: <Code size={13} /> },
                      { label: 'Coding Test',     value: c.codingScore,        icon: <Code size={13} /> },
                      { label: 'Communication',   value: c.communicationScore, icon: <MessageSquare size={13} /> },
                      { label: 'HR Interview',    value: c.hrScore,            icon: <Users size={13} /> },
                      { label: 'Trust Score',     value: c.trustScore,         icon: <Shield size={13} /> },
                    ].filter(r => r.value != null);

                    return (
                      <Grid item xs={12} sm={6} md={Math.max(3, Math.floor(12 / selectedCandidates.length))} key={c.application_id}>
                        <Box sx={{
                          p: 2, borderRadius: '14px',
                          border: `1.5px solid ${color}25`,
                          background: `linear-gradient(135deg, ${color}06, ${color}02)`,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: color, fontSize: '0.65rem', fontWeight: 700 }}>
                              {getInitials(c.name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827' }}>{c.name}</Typography>
                              <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>Overall: {c.overallScore || 0}/100</Typography>
                            </Box>
                          </Box>
                          {scoreRows.length > 0 ? scoreRows.map(r => (
                            <Box key={r.label} sx={{ mb: 0.8 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                <Box sx={{ color: '#9ca3af' }}>{r.icon}</Box>
                                <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>{r.label}</Typography>
                              </Box>
                              <ScoreBar value={r.value} color={color} />
                            </Box>
                          )) : (
                            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                              No interview scores available yet.
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
